<script setup lang="ts">
import { computed } from 'vue';
import { Badge } from '../badge';
import { FileThumbnail } from '../file-thumbnail';
import { FileTypeIcon } from '../file-type-icon';
import {
  formatFileDate,
  formatFileSize,
  getFileNameExtension,
} from '../../utils/file';

type Props = {
  /** Display name of the file; its extension drives the glyph and chip unless `extension` is set. */
  fileName: string;
  /** Extension to use for the glyph and chip when the display name has none (e.g. a curated title). */
  extension?: string;
  /** File size in bytes; the meta line omits it when null/undefined. */
  sizeBytes?: number | null;
  /** ISO upload date; the meta line omits it when null/undefined. */
  uploadedAt?: string | null;
  customPreviewUrl?: string | null;
  previewUrl?: string | null;
  fileUrl?: string | null;
  resolveImageUrl?: (url: string) => string;
  /** Single-line row with the file-type glyph instead of a thumbnail, for tight spaces like modal previews. */
  compact?: boolean;
};

const props = defineProps<Props>();

const extensionLabel = computed(
  () => props.extension?.toLowerCase() || getFileNameExtension(props.fileName),
);

const metaLabel = computed(() =>
  [
    props.sizeBytes ? formatFileSize(props.sizeBytes) : null,
    props.uploadedAt ? formatFileDate(props.uploadedAt) : null,
  ]
    .filter(Boolean)
    .join(' · '),
);
</script>

<template>
  <div v-if="compact" class="flex items-center justify-between gap-lg p-xl">
    <div class="flex min-w-0 items-center gap-xl">
      <slot name="leading" />
      <div class="flex min-w-0 items-center gap-lg">
        <FileTypeIcon :file-name="fileName" :extension="extensionLabel" />
        <div class="min-w-0">
          <p class="truncate font-bold text-on-surface-primary">
            {{ fileName }}
          </p>
          <div class="mt-xs flex items-center gap-md">
            <Badge v-if="extensionLabel" :label="extensionLabel" />
            <span v-if="metaLabel" class="text-xs text-on-surface-secondary">
              {{ metaLabel }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex shrink-0 items-center">
      <slot name="actions" />
    </div>
  </div>
  <div
    v-else
    class="flex items-start justify-between gap-md p-xl sm:items-center sm:gap-lg"
  >
    <div
      class="flex min-w-0 flex-1 items-start gap-md sm:items-center sm:gap-xl"
    >
      <slot name="leading" />
      <div class="flex min-w-0 flex-1 items-start gap-lg sm:items-center">
        <FileThumbnail
          :file-name="fileName"
          :extension="extensionLabel"
          :custom-preview-url="customPreviewUrl"
          :preview-url="previewUrl"
          :file-url="fileUrl"
          :resolve-image-url="resolveImageUrl"
          class="h-[48px] w-[85px] sm:h-[90px] sm:w-[160px]"
        />
        <div class="min-w-0">
          <p
            class="line-clamp-2 font-bold break-words text-on-surface-primary sm:line-clamp-1"
          >
            {{ fileName }}
          </p>
          <div
            class="mt-xs flex flex-col items-start gap-md sm:flex-row sm:items-center"
          >
            <Badge v-if="extensionLabel" :label="extensionLabel" />
            <span v-if="metaLabel" class="text-xs text-on-surface-secondary">
              {{ metaLabel }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex shrink-0 items-center">
      <slot name="actions" />
    </div>
  </div>
</template>
